import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Eye, Lock, Globe, ArrowLeft, Heart } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import WishlistDealTile from "../components/WishlistDealTile";
import { useAuth } from "../lib/AuthContext";

// Set/remove OG meta tags on the document head so scrapers that execute JS (Discord, LinkedIn, Slack) get rich previews.
function setOgMeta({ title, description, image, url }) {
  const upsert = (attr, key, value) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
    el.setAttribute("content", value || "");
  };
  document.title = title;
  upsert("property", "og:title", title);
  upsert("property", "og:description", description);
  upsert("property", "og:image", image);
  upsert("property", "og:url", url);
  upsert("property", "og:type", "website");
  upsert("name", "twitter:card", "summary_large_image");
  upsert("name", "twitter:title", title);
  upsert("name", "twitter:description", description);
  upsert("name", "twitter:image", image);
}

export default function PublicWishlist() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [wl, setWl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/wishlists/${slug}`);
        setWl(data);
        // Fetch follow status
        try {
          const fs = await api.get(`/wishlists/${slug}/follow-status`);
          setFollowerCount(fs.data?.followers || 0);
          setFollowing(!!fs.data?.following);
        } catch (e) { /* non-fatal */ }
        // Set OG meta tags for rich share previews
        const apiBase = api.defaults.baseURL || "";
        const coverUrl = `${apiBase}/wishlists/${slug}/cover.png`;
        setOgMeta({
          title: `${data.name} — DealHub wishlist`,
          description: data.description || `${data.deal_ids?.length || 0} curated deals from ${data.owner_name || "a DealHub shopper"}.`,
          image: coverUrl,
          url: window.location.href,
        });
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
    return () => { document.title = "DealHub"; };
  }, [slug]);

  const toggleFollow = async () => {
    if (authLoading) return;
    if (!user) {
      toast.error("Log in to follow wishlists.");
      navigate("/login");
      return;
    }
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/wishlists/${slug}/follow`);
      const isFollowing = data.action === "followed";
      setFollowing(isFollowing);
      setFollowerCount(data.followers || 0);
      toast.success(isFollowing ? "Following — we'll email you when new picks are added." : "Unfollowed.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFollowBusy(false);
    }
  };

  const copy = async () => {
    const apiBase = api.defaults.baseURL || "";
    // Share the SSR endpoint so Facebook/Twitter/etc. see OG tags without executing JS.
    // Browsers hit this URL and get an instant redirect to /w/{slug}.
    const url = `${apiBase}/share/wishlist/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — share it anywhere.");
    } catch (e) { toast.error("Couldn't copy — long-press to share."); }
  };

  if (loading) return <div className="max-w-6xl mx-auto px-6 py-16 text-xs font-mono uppercase tracking-widest text-black/40">Loading wishlist…</div>;
  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="wishlist-not-found">
      <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3">404 &middot; No such wishlist</div>
      <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter mb-4">This list doesn&rsquo;t exist</h1>
      <p className="text-black/60 mb-6">The owner may have deleted it or made it private.</p>
      <Link to="/" className="inline-block bg-black text-white px-5 py-3 text-xs font-bold uppercase tracking-[0.25em] hover:bg-[#E63946]">Browse DealHub</Link>
    </div>
  );
  if (!wl) return null;

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid={`public-wishlist-${slug}`}>
      <Link to="/" className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-[#E63946]">
        <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
        Back to DealHub
      </Link>

      <div className="mt-6 mb-10 border-b border-black/10 pb-8">
        <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-3 flex-wrap">
          <span className="text-[#E63946]">Wishlist</span>
          <span className="w-1 h-1 bg-black/30 rounded-full" />
          <span>{wl.deals?.length || 0} deals</span>
          {wl.is_public && (<><span className="w-1 h-1 bg-black/30 rounded-full" /><span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" strokeWidth={2} />Public</span></>)}
          {!wl.is_public && (<><span className="w-1 h-1 bg-black/30 rounded-full" /><span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" strokeWidth={2} />Private</span></>)}
          <span className="w-1 h-1 bg-black/30 rounded-full" />
          <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" strokeWidth={2} />{wl.view_count} views</span>
          {followerCount > 0 && (
            <>
              <span className="w-1 h-1 bg-black/30 rounded-full" />
              <span className="inline-flex items-center gap-1" data-testid="wishlist-follower-count">
                <Heart className="w-3 h-3" strokeWidth={2} />
                {followerCount} follower{followerCount === 1 ? "" : "s"}
              </span>
            </>
          )}
        </div>
        <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter" data-testid="wishlist-title">
          {wl.name}
        </h1>
        {wl.description && (
          <p className="mt-3 text-black/60 font-serif italic max-w-2xl" data-testid="wishlist-description">
            {wl.description}
          </p>
        )}
        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <div className="text-[10px] font-mono uppercase tracking-widest text-black/50">
            Curated by <span className="text-black font-bold">{wl.owner_name}</span>
          </div>
          <button
            onClick={copy}
            className="inline-flex items-center gap-2 border-2 border-black bg-white text-black text-xs font-bold uppercase tracking-[0.25em] px-4 py-2 hover:bg-black hover:text-white"
            data-testid="wishlist-copy-link-btn"
          >
            <Copy className="w-3.5 h-3.5" strokeWidth={2} />
            Copy link
          </button>
          {!wl.is_owner && (
            <button
              onClick={toggleFollow}
              disabled={followBusy}
              className={`inline-flex items-center gap-2 border-2 text-xs font-bold uppercase tracking-[0.25em] px-4 py-2 disabled:opacity-50 ${
                following
                  ? "bg-[#111111] text-white border-[#111111] hover:bg-[#E63946] hover:border-[#E63946]"
                  : "bg-white text-black border-black hover:bg-black hover:text-white"
              }`}
              data-testid="wishlist-follow-btn"
            >
              <Heart className="w-3.5 h-3.5" strokeWidth={2} fill={following ? "currentColor" : "none"} />
              {following ? "Following" : "Follow"}
            </button>
          )}
        </div>
      </div>

      {(wl.deals || []).length === 0 ? (
        <div className="border border-dashed border-black/20 p-12 text-center bg-white" data-testid="wishlist-empty">
          <div className="font-serif text-2xl mb-2">This list is empty</div>
          <p className="text-black/60 text-sm">All the bundled deals have expired or been removed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="wishlist-deals-grid">
          {wl.deals.map((d) => (
            <WishlistDealTile key={d.id} slug={slug} deal={d} isWishlistOwner={!!wl.is_owner} emojis={wl.reaction_emojis} />
          ))}
        </div>
      )}

      <div className="mt-16 border-t border-black/10 pt-10 text-center">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/40 mb-3">Like these picks?</div>
        <Link to="/register" className="inline-block bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-6 py-3 hover:bg-[#E63946]" data-testid="wishlist-signup-cta">
          Join DealHub — save your own
        </Link>
      </div>
    </div>
  );
}
